/**
 * usePolling.ts — Hook genérico de polling con soporte de Page Visibility API.
 *
 * QUÉ HACE:
 *   - Ejecuta un callback en intervalos regulares.
 *   - Pausa automáticamente cuando el usuario cambia de pestaña (visibilityState).
 *   - Refresca inmediatamente al volver a la pestaña activa.
 *   - Puede activarse/desactivarse con `enabled`.
 *
 * QUÉ EXPORTA: usePolling
 *
 * QUÉ NO DEBE HACER:
 *   - NO contiene lógica de negocio.
 *   - NO accede a ningún dato específico del dominio.
 *   - NO importa servicios ni stores.
 *
 * Uso:
 *   usePolling(fetchProductos, { interval: 30_000 });
 *   usePolling(fetchConfig,    { interval: 30_000, runImmediately: false });
 */
import { useEffect, useRef, useCallback } from "react";

export interface UsePollingOptions {
  /** Intervalo en milisegundos entre ejecuciones. */
  interval: number;
  /** Si es false, detiene el polling. Útil para suspenderlo condicionalmente. Default: true */
  enabled?: boolean;
  /**
   * Si es true, ejecuta el callback inmediatamente al montar (o al re-activarse).
   * Default: false — útil cuando ya hay una carga inicial explícita.
   */
  runImmediately?: boolean;
}

export const usePolling = (
  callback: () => void | Promise<void>,
  { interval, enabled = true, runImmediately = false }: UsePollingOptions,
): void => {
  // Ref para mantener siempre la versión más reciente del callback sin reiniciar el timer.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    stop();
    timerRef.current = setInterval(() => {
      // Solo ejecuta si el tab está visible — evita llamadas innecesarias en background.
      if (document.visibilityState === "visible") {
        callbackRef.current();
      }
    }, interval);
  }, [interval, stop]);

  useEffect(() => {
    if (!enabled) {
      stop();
      return;
    }

    if (runImmediately) {
      callbackRef.current();
    }

    start();

    // Al volver al tab: refresca inmediatamente para que el usuario vea datos frescos.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        callbackRef.current();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, start, stop, runImmediately]);
};
